import React, { useState, useEffect, useRef } from 'react'
import { cleanStayNumber } from './fieldSearch'
import { fetchStayByNumber, fetchClients } from './fieldApi'
import { guestName, stayDatesLabel } from './fieldStays'
import { nowSeconds } from './fieldDates'
import { SearchIcon, NoResultIcon, SuitcaseIcon, StayIllustration } from './FieldIcons'
import { BackButton, FlowHeading, CompactFlowHeader, ListGroup, ListRow, SearchField, HelpText, Notice, LinkButton, LoadingText, visuallyHidden, scrollFieldToTop } from './FieldUI'
import { colors } from './fieldTheme'

// Saisie d'un numéro de séjour (canvas W5c), proposée seulement pour l'entrée et la sortie (décision 2).
// Même mode compact que la recherche d'emplacement dès le premier chiffre.

const SEARCH_DELAY = 400

export default function FieldStayNumber({ type, onSelect, onPitchSearch, onBack })
{
	const [number, setNumber] = useState('')
	const [status, setStatus] = useState('idle')
	const [stay, setStay] = useState(null)
	const [client, setClient] = useState(null)
	const inputRef = useRef(null)

	useEffect(() => {
		if (inputRef.current)
			inputRef.current.focus()
	}, [])

	useEffect(() => {
		if (number === '')
		{
			setStatus('idle')
			return ;
		}
		let cancelled = false
		setStatus('loading')
		const timer = setTimeout(() => {
			fetchStayByNumber(number)
				.then(found => {
					if (cancelled)
						return null
					setStay(found)
					setClient(null)
					setStatus(found ? 'found' : 'notfound')
					return found ? fetchClients([found]) : null
				})
				.then(clients => {
					if (!cancelled && clients)
						setClient(Object.values(clients)[0] || null)
				})
				.catch(() => {
					if (!cancelled)
						setStatus('error')
				})
		}, SEARCH_DELAY)
		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [number])

	const compact = number !== ''

	useEffect(() => {
		if (compact)
			scrollFieldToTop(inputRef.current)
	}, [compact])

	const clear = () => {
		setNumber('')
		if (inputRef.current)
			inputRef.current.focus()
	}

	const now = nowSeconds()
	const name = guestName(client)
	const details = stay ? [name, stay.location, stayDatesLabel(stay, now)].filter(Boolean).join('\n') : ''

	return (
		<div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingBottom: 32 }}>
			{compact && <CompactFlowHeader type={type} backLabel="Accueil" onBack={onBack} />}
			{!compact && <BackButton label="Accueil" onClick={onBack} />}
			{!compact && <FlowHeading type={type} />}
			<label htmlFor="field-stay" style={compact ? visuallyHidden : { display: 'block', fontSize: 17, fontWeight: 700, color: colors.text, padding: '32px 20px 12px' }}>Numéro de séjour</label>
			{compact && <div aria-hidden="true" style={{ height: 12, flex: 'none' }} />}
			<SearchField
				id="field-stay"
				inputRef={inputRef}
				inputMode="numeric"
				icon={<span style={{ color: colors.text, display: 'flex' }}><SuitcaseIcon /></span>}
				value={number}
				onChange={(value) => setNumber(cleanStayNumber(value))}
				onClear={clear}
				onEnter={() => {
					if (status === 'found' && stay)
						onSelect(stay)
				}}
				clearLabel="Effacer le numéro"
			/>
			{!compact && <HelpText>Chiffres uniquement : le clavier numérique s'ouvre.</HelpText>}

			{status === 'loading' && <LoadingText>Recherche du séjour…</LoadingText>}
			{status === 'found' && stay && (
				<React.Fragment>
					<h2 style={visuallyHidden}>Résultat</h2>
					<div aria-hidden="true" style={{ height: 16, flex: 'none' }} />
					<ListGroup>
						<ListRow
							icon={<StayIllustration />}
							iconBackground="#E8EBF5"
							title={'Séjour n° ' + stay.stay_number}
							subtitle={details}
							minHeight={80}
							onClick={() => onSelect(stay)}
						/>
					</ListGroup>
				</React.Fragment>
			)}
			{status === 'notfound' && (
				<Notice icon={<NoResultIcon />} title={'Aucun séjour n° « ' + number + ' »'} hint="Vérifiez le numéro sur la fiche du séjour." />
			)}
			{status === 'error' && (
				<Notice icon={<NoResultIcon />} title="Impossible de chercher le séjour" hint="Vérifiez la connexion, puis réessayez." />
			)}

			<div style={{ padding: '16px 8px 0' }}>
				<LinkButton icon={<SearchIcon color="currentColor" />} onClick={onPitchSearch}>Saisir un emplacement plutôt</LinkButton>
			</div>
		</div>
	)
}
